import React, { useState, useEffect } from 'react'
import { useAuth } from '../AuthProvider'
import { useNavigate, Link } from 'react-router-dom'
import { Sparkles, Loader, BookOpen, User, Send, ChevronRight, Eye, Copy, Check, Trash2, Plus, ExternalLink, Share2, EyeOff, Users, AlertCircle, Brain } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { usePlanLimits } from '../hooks/usePlanLimits'
import { toast } from 'react-hot-toast'

interface AIIdea {
  id: string
  book_title: string
  author: string
  generated_content: any
  created_at: string
  shared_with_team?: boolean
  is_hidden?: boolean
  is_owner?: boolean
  author_info?: {
    id: string
    email: string
    full_name: string
  }
}

interface AIJob {
  id: string
  book_title: string
  author: string
  status: 'pending' | 'processing' | 'completed' | 'failed'
  created_at: string
  error?: string
}

interface ChapterIdea {
  chapter_number: number
  chapter_title: string
  chapter_summary: string
  qr_ideas: QRIdea[]
}

interface QRIdea {
  idea_type: string
  reader_value: string
  implementation: string
  qr_data: any
}

export default function AIIdeas() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const { canUseAIIdeas, getAIUsageInfo } = usePlanLimits()
  const [loading, setLoading] = useState(true)
  const [isGenerating, setIsGenerating] = useState(false)
  const [ideas, setIdeas] = useState<AIIdea[]>([])
  const [pendingJobs, setPendingJobs] = useState<AIJob[]>([])
  const [selectedIdea, setSelectedIdea] = useState<AIIdea | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedChapter, setSelectedChapter] = useState<ChapterIdea | null>(null)
  const [showHidden, setShowHidden] = useState(false)
  
  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'

  const [formData, setFormData] = useState({
    bookTitle: '',
    author: ''
  })

  const [generationError, setGenerationError] = useState<string | null>(null)

  // Calculate days until reset
  const calculateResetsIn = (resetAt: string | undefined) => {
    if (!resetAt) return null
    const resetDate = new Date(resetAt)
    const now = new Date()
    const diffTime = resetDate.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays > 0 ? diffDays : null
  }

  useEffect(() => {
    fetchIdeas()
    checkPendingJobs()
  }, [])

  const fetchIdeas = async () => {
    if (!session?.access_token) return
    
    setLoading(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3002'}/api/ai-ideas`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setIdeas(data)
      }
    } catch (error) {
      console.error('Error fetching ideas:', error)
    } finally {
      setLoading(false)
    }
  }

  const generateIdeas = async () => {
    if (!formData.bookTitle.trim() || !formData.author.trim()) {
      toast.error('Bitte geben Sie Buchtitel und Autor ein')
      return
    }

    setIsGenerating(true)
    setGenerationError(null)

    try {
      // Start async job
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/ai-ideas/generate-async`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          bookTitle: formData.bookTitle.trim(),
          author: formData.author.trim()
        })
      })

      const data = await response.json()

      if (!response.ok) {
        if (data.upgrade_required) {
          setGenerationError('KI-Buchanalysen sind in Ihrem aktuellen Plan nicht verfügbar. Bitte upgraden Sie auf Growth oder Business.')
        } else if (data.limit_reached) {
          setGenerationError(data.error)
        } else {
          setGenerationError(data.error || 'Fehler bei der Generierung')
        }
        toast.error(data.error || 'Fehler bei der Generierung')
        setIsGenerating(false)
        return
      }

      // Start polling for job status
      const jobId = data.jobId
      
      // Add to pending jobs
      const newJob: AIJob = {
        id: jobId,
        book_title: formData.bookTitle.trim(),
        author: formData.author.trim(),
        status: 'pending',
        created_at: new Date().toISOString()
      }
      setPendingJobs(prev => [...prev, newJob])
      
      toast('KI-Analyse läuft im Hintergrund. Sie können die Seite verlassen und später wiederkommen.', {
        icon: '🤖',
        duration: 6000
      })
      
      // Clear form immediately so user can start another one
      setFormData({
        bookTitle: '',
        author: ''
      })
      setIsGenerating(false)
      
      // Poll every 2 seconds
      pollJobStatus(jobId)

    } catch (error) {
      console.error('Error generating ideas:', error)
      setGenerationError('Netzwerkfehler bei der Generierung')
      toast.error('Netzwerkfehler bei der Generierung')
      setIsGenerating(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!session?.access_token) return
    if (!confirm('Möchten Sie diese AI-Idee wirklich löschen?')) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3002'}/api/ai-ideas/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (response.ok) {
        await fetchIdeas()
        if (selectedIdea?.id === id) {
          setSelectedIdea(null)
        }
      }
    } catch (error) {
      console.error('Error deleting idea:', error)
    }
  }

  const handleShare = async (id: string, currentlyShared: boolean) => {
    if (!session?.access_token) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3002'}/api/ai-ideas/${id}/share`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ shared: !currentlyShared })
      })
      
      if (response.ok) {
        await fetchIdeas()
      }
    } catch (error) {
      console.error('Error sharing idea:', error)
    }
  }

  const handleToggleVisibility = async (id: string, currentlyHidden: boolean) => {
    if (!session?.access_token) return
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3002'}/api/ai-ideas/${id}/visibility`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ hidden: !currentlyHidden })
      })
      
      if (response.ok) {
        await fetchIdeas()
      }
    } catch (error) {
      console.error('Error toggling visibility:', error)
    }
  }

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text)
    setCopied(id)
    setTimeout(() => setCopied(null), 2000)
  }

  // Check for pending jobs from localStorage and backend
  const checkPendingJobs = async () => {
    // First check localStorage
    const savedJobs = localStorage.getItem('ai_pending_jobs')
    let localJobs: AIJob[] = []
    
    if (savedJobs) {
      const jobs: AIJob[] = JSON.parse(savedJobs)
      // Only keep jobs from the last 24 hours
      localJobs = jobs.filter(job => {
        const jobAge = Date.now() - new Date(job.created_at).getTime()
        return jobAge < 24 * 60 * 60 * 1000 // 24 hours
      })
    }
    
    // Also check backend for any jobs not in localStorage
    if (session?.access_token) {
      try {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/ai-ideas/jobs`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        })
        
        if (response.ok) {
          const backendJobs = await response.json()
          
          // Merge with local jobs, avoiding duplicates
          const localJobIds = new Set(localJobs.map(j => j.id))
          const newJobs = backendJobs.filter((job: any) => !localJobIds.has(job.id))
          
          const allJobs = [...localJobs, ...newJobs]
          
          if (allJobs.length > 0) {
            setPendingJobs(allJobs)
            // Start polling for each pending job
            allJobs.forEach(job => {
              if (job.status === 'pending' || job.status === 'processing') {
                pollJobStatus(job.id)
              }
            })
          }
        }
      } catch (error) {
        console.error('Error fetching backend jobs:', error)
        // Fall back to just local jobs
        if (localJobs.length > 0) {
          setPendingJobs(localJobs)
          localJobs.forEach(job => {
            if (job.status === 'pending' || job.status === 'processing') {
              pollJobStatus(job.id)
            }
          })
        }
      }
    } else if (localJobs.length > 0) {
      // No session, just use local jobs
      setPendingJobs(localJobs)
      localJobs.forEach(job => {
        if (job.status === 'pending' || job.status === 'processing') {
          pollJobStatus(job.id)
        }
      })
    }
  }

  // Poll job status
  const pollJobStatus = (jobId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/ai-ideas/job/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`
          }
        })

        if (!statusResponse.ok) {
          clearInterval(pollInterval)
          // Update job status to failed
          setPendingJobs(prev => prev.map(j => 
            j.id === jobId ? { ...j, status: 'failed', error: 'Fehler beim Abrufen des Status' } : j
          ))
          return
        }

        const statusData = await statusResponse.json()
        const job = statusData.job

        // Update job status in state
        setPendingJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, status: job.status, error: job.error } : j
        ))

        if (job.status === 'completed') {
          clearInterval(pollInterval)
          toast.success('KI-Buchanalyse erfolgreich generiert!')
          
          // Refresh the ideas list
          await fetchIdeas()
          
          // Remove from pending jobs after a delay
          setTimeout(() => {
            setPendingJobs(prev => prev.filter(j => j.id !== jobId))
          }, 2000)
        } else if (job.status === 'failed') {
          clearInterval(pollInterval)
          toast.error(job.error || 'Fehler bei der KI-Generierung')
          
          // Remove from pending jobs after a delay
          setTimeout(() => {
            setPendingJobs(prev => prev.filter(j => j.id !== jobId))
          }, 5000)
        }
      } catch (error) {
        console.error('Polling error:', error)
        clearInterval(pollInterval)
        setPendingJobs(prev => prev.map(j => 
          j.id === jobId ? { ...j, status: 'failed', error: 'Verbindungsfehler' } : j
        ))
      }
    }, 2000) // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 5 * 60 * 1000)
  }

  // Save pending jobs to localStorage whenever they change
  useEffect(() => {
    if (pendingJobs.length > 0) {
      localStorage.setItem('ai_pending_jobs', JSON.stringify(pendingJobs))
    } else {
      localStorage.removeItem('ai_pending_jobs')
    }
  }, [pendingJobs])

  const getQRTypeFromIdea = (ideaType: string): string => {
    const typeMap: Record<string, string> = {
      'Spotify Playlist': 'spotify',
      'YouTube Video': 'youtube',
      'PDF Dokument': 'pdf',
      'Website': 'website',
      'Multi-Shop': 'multishop',
      'Autorenprofil': 'author',
      'Leseprobe': 'sample'
    }
    return typeMap[ideaType] || 'website'
  }

  const handleCreateQR = (qrIdea: QRIdea, chapter: ChapterIdea) => {
    setSelectedChapter({
      ...chapter,
      selectedQRIdea: qrIdea
    } as any)
    setShowCreateModal(true)
  }

  const navigateToCreateQR = () => {
    if (!selectedChapter || !selectedIdea) return
    
    const qrIdea = (selectedChapter as any).selectedQRIdea as QRIdea
    if (!qrIdea) return
    
    // Navigate to Links page with pre-filled data
    navigate('/qr/dash/links', {
      state: {
        createNew: true,
        type: qrIdea.qr_data.type || getQRTypeFromIdea(qrIdea.idea_type),
        title: qrIdea.qr_data.title || `${selectedIdea.book_title} - ${qrIdea.idea_type}`,
        bookTitle: selectedIdea.book_title,
        author: selectedIdea.author,
        qrData: qrIdea.qr_data,
        aiGeneratedContent: qrIdea
      }
    })
  }

  const renderQRIdea = (qrIdea: QRIdea, chapter: ChapterIdea, index: number) => {
    const qrTypeIcons: Record<string, React.ReactNode> = {
      'Spotify Playlist': '🎵',
      'YouTube Video': '📹',
      'PDF Dokument': '📄',
      'Website': '🌐',
      'Multi-Shop': '🛒',
      'Autorenprofil': '👤',
      'Leseprobe': '📖'
    }

    const qrType = qrIdea.qr_data?.type || getQRTypeFromIdea(qrIdea.idea_type)

    return (
      <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{qrTypeIcons[qrIdea.idea_type] || '📱'}</span>
            <h4 className="font-semibold">{qrIdea.idea_type}</h4>
          </div>
          <button
            onClick={() => copyToClipboard(JSON.stringify(qrIdea.qr_data, null, 2), `qr-${chapter.chapter_number}-${index}`)}
            className="text-gray-500 hover:text-gray-700"
            title="QR-Daten kopieren"
          >
            {copied === `qr-${chapter.chapter_number}-${index}` ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
        
        <div className="space-y-2 text-sm">
          <div>
            <span className="font-medium text-gray-600">Mehrwert:</span>
            <p className="text-gray-800 mt-1">{qrIdea.reader_value}</p>
          </div>
          
          <div>
            <span className="font-medium text-gray-600">Umsetzung:</span>
            <p className="text-gray-800 mt-1">{qrIdea.implementation}</p>
          </div>

          {qrIdea.qr_data && (
            <div className="mt-2 p-2 bg-gray-100 rounded text-xs space-y-1">
              <div><span className="font-medium">QR-Typ:</span> <span className="bg-white px-2 py-0.5 rounded">{qrType}</span></div>
              {qrIdea.qr_data.title && <div><span className="font-medium">Titel:</span> {qrIdea.qr_data.title}</div>}
              {qrIdea.qr_data.target_url && <div><span className="font-medium">URL:</span> <span className="text-blue-600 truncate inline-block max-w-xs">{qrIdea.qr_data.target_url}</span></div>}
              {qrIdea.qr_data.pdf_url && <div><span className="font-medium">PDF:</span> Wird beim Erstellen hochgeladen</div>}
            </div>
          )}
        </div>
        
        <button 
          onClick={() => handleCreateQR(qrIdea, chapter)}
          className="mt-3 text-sm bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-1"
        >
          <Plus className="w-4 h-4" />
          QR-Code erstellen
        </button>
      </div>
    )
  }

  // Filter ideas based on visibility
  const visibleIdeas = showHidden ? ideas : ideas.filter(idea => !idea.is_hidden)

  // Get AI usage info
  const aiUsageInfo = getAIUsageInfo()

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-yellow-500" />
                AI QR-Code Ideen Generator
              </h1>
              <p className="text-gray-600 mt-2">
                Lassen Sie KI kreative QR-Code Ideen für Ihr Buch generieren
              </p>
            </div>
            {aiUsageInfo && aiUsageInfo.limit > 0 && aiUsageInfo.used >= aiUsageInfo.limit && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-amber-900">
                      KI-Buchanalysen-Limit erreicht
                    </h3>
                    <p className="text-sm text-amber-700 mt-1">
                      Sie haben Ihr monatliches Limit von {aiUsageInfo.limit} KI-Buchanalysen erreicht.
                      {aiUsageInfo.reset_at && calculateResetsIn(aiUsageInfo.reset_at) && (
                        <span className="block mt-1">
                          Zurücksetzung in {calculateResetsIn(aiUsageInfo.reset_at)} Tagen.
                        </span>
                      )}
                    </p>
                    <Link
                      to="/qr/dash/profile#billing"
                      className="inline-flex items-center text-sm font-medium text-amber-800 hover:text-amber-900 mt-2"
                    >
                      Plan upgraden für mehr KI-Buchanalysen →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* No access warning */}
        {!canUseAIIdeas() && (
          <div className="mb-6 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-yellow-800">AI-Ideen nicht verfügbar</h3>
                <p className="text-yellow-700 mt-1">
                  AI-Ideen sind ab dem Growth-Plan verfügbar. Upgraden Sie jetzt, um diese Funktion zu nutzen.
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-2 text-sm bg-yellow-600 text-white px-3 py-1 rounded hover:bg-yellow-700"
                >
                  Jetzt upgraden
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Form */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-semibold mb-4">Buchinformationen</h2>
            
            <form onSubmit={(e) => { e.preventDefault(); generateIdeas() }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <BookOpen className="w-4 h-4 inline mr-1" />
                  Buchtitel *
                </label>
                <input
                  type="text"
                  value={formData.bookTitle}
                  onChange={(e) => setFormData({ ...formData, bookTitle: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. Die Magie der Ghibli-Küche"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="w-4 h-4 inline mr-1" />
                  Autor *
                </label>
                <input
                  type="text"
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. Max Mustermann"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isGenerating || !formData.bookTitle || !formData.author}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Generiere KI-Buchanalysen...
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4" />
                    KI-Buchanalysen generieren
                  </>
                )}
              </button>
            </form>

            {generationError && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{generationError}</p>
              </div>
            )}

            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">So funktioniert's:</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Geben Sie Buchtitel und Autor ein</li>
                <li>KI generiert kreative QR-Code Ideen für Ihr Buch</li>
                <li>Wählen Sie die besten Ideen aus</li>
                <li>Erstellen Sie QR-Codes mit einem Klick</li>
              </ol>
            </div>
          </div>

          {/* Results Display */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Generierte Ideen</h2>
              {selectedIdea && (
                <div className="flex items-center gap-2">
                  {selectedIdea.is_owner ? (
                    <>
                      <button
                        onClick={() => handleShare(selectedIdea.id, selectedIdea.shared_with_team || false)}
                        className={`px-3 py-1 rounded flex items-center gap-1 ${
                          selectedIdea.shared_with_team 
                            ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={selectedIdea.shared_with_team ? 'Freigabe entfernen' : 'Mit Team teilen'}
                      >
                        <Share2 className="w-4 h-4" />
                        {selectedIdea.shared_with_team ? 'Geteilt' : 'Teilen'}
                      </button>
                      <button
                        onClick={() => handleDelete(selectedIdea.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Idee löschen"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleToggleVisibility(selectedIdea.id, selectedIdea.is_hidden || false)}
                      className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-3 py-1 rounded flex items-center gap-1"
                    >
                      {selectedIdea.is_hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      {selectedIdea.is_hidden ? 'Einblenden' : 'Ausblenden'}
                    </button>
                  )}
                </div>
              )}
            </div>
            
            {selectedIdea ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h3 className="font-semibold text-blue-900">{selectedIdea.book_title}</h3>
                  <p className="text-sm text-blue-700">von {selectedIdea.author}</p>
                  {!selectedIdea.is_owner && selectedIdea.author_info && (
                    <p className="text-sm text-blue-600 mt-1">
                      Geteilt von {selectedIdea.author_info.full_name || selectedIdea.author_info.email}
                    </p>
                  )}
                  <p className="text-sm text-blue-600 mt-1">
                    Generiert am {new Date(selectedIdea.created_at).toLocaleDateString('de-DE')}
                  </p>
                </div>

                {selectedIdea.generated_content?.chapters ? (
                  selectedIdea.generated_content.chapters.map((chapter: ChapterIdea) => (
                    <div key={chapter.chapter_number} className="border rounded-lg p-4">
                      <h3 className="text-lg font-semibold mb-2">
                        Kapitel {chapter.chapter_number}: {chapter.chapter_title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">{chapter.chapter_summary}</p>
                      
                      <div className="space-y-3">
                        {chapter.qr_ideas.map((qrIdea, idx) => renderQRIdea(qrIdea, chapter, idx))}
                      </div>
                    </div>
                  ))
                ) : (
                  // Fallback for old format (Kapiteluebersicht_digitale_Zusatzinhalte)
                  selectedIdea.generated_content?.Kapiteluebersicht_digitale_Zusatzinhalte ? (
                    selectedIdea.generated_content.Kapiteluebersicht_digitale_Zusatzinhalte.map((chapter: any, idx: number) => (
                      <div key={idx} className="border-b pb-4 last:border-b-0">
                        <div className="prose prose-sm max-w-none mb-3">
                          <ReactMarkdown>
                            {chapter.Kapitel}
                          </ReactMarkdown>
                        </div>
                        {/* Legacy rendering for old format */}
                        <div className="bg-yellow-50 p-3 rounded text-sm">
                          <p className="font-medium">Altes Format - Bitte neu generieren für volle Funktionalität</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 text-center py-8">
                      <p>Keine Kapitelübersicht gefunden</p>
                      <pre className="mt-4 text-xs text-left bg-gray-100 p-2 rounded overflow-auto">
                        {JSON.stringify(selectedIdea.generated_content, null, 2)}
                      </pre>
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Sparkles className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Noch keine Ideen generiert</p>
                <p className="text-sm mt-1">Füllen Sie das Formular aus und klicken Sie auf "Ideen generieren"</p>
              </div>
            )}
          </div>
        </div>

        {/* History Section */}
        {(ideas.length > 0 || pendingJobs.length > 0) && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Ihre generierten Ideen</h2>
              <button
                onClick={() => setShowHidden(!showHidden)}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                {showHidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                {showHidden ? 'Ausgeblendete anzeigen' : 'Ausgeblendete verbergen'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Show pending jobs first */}
              {pendingJobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-lg shadow-sm p-4 border-2 border-dashed border-gray-300 relative overflow-hidden"
                >
                  {/* Animated background for processing */}
                  {job.status === 'processing' && (
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-transparent animate-pulse"></div>
                  )}
                  
                  <div className="relative">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-semibold truncate flex-1">{job.book_title}</h3>
                      <Loader className={`w-4 h-4 ml-2 ${job.status === 'processing' ? 'animate-spin text-blue-600' : 'text-gray-400'}`} />
                    </div>
                    <p className="text-sm text-gray-600">von {job.author}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(job.created_at).toLocaleDateString('de-DE')}
                    </p>
                    
                    <div className="mt-3 flex items-center gap-2">
                      <div className={`text-sm px-3 py-1 rounded-full flex items-center gap-2 ${
                        job.status === 'pending' ? 'bg-gray-100 text-gray-700' :
                        job.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                        job.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {job.status === 'pending' && <>
                          <Loader className="w-3 h-3" />
                          Wartend
                        </>}
                        {job.status === 'processing' && <>
                          <Loader className="w-3 h-3 animate-spin" />
                          In Bearbeitung
                        </>}
                        {job.status === 'failed' && <>
                          <AlertCircle className="w-3 h-3" />
                          Fehlgeschlagen
                        </>}
                        {job.status === 'completed' && <>
                          <Check className="w-3 h-3" />
                          Abgeschlossen
                        </>}
                      </div>
                    </div>
                    
                    {job.error && (
                      <p className="text-xs text-red-600 mt-2">{job.error}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Then show completed ideas */}
              {visibleIdeas.map((idea) => (
                <div
                  key={idea.id}
                  className={`bg-white rounded-lg shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${
                    selectedIdea?.id === idea.id ? 'ring-2 ring-blue-500' : ''
                  } ${idea.is_hidden ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedIdea(idea)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold truncate flex-1">{idea.book_title}</h3>
                    <div className="flex items-center gap-1 ml-2">
                      {idea.shared_with_team && (
                        <span title="Mit Team geteilt">
                          <Users className="w-4 h-4 text-blue-600" />
                        </span>
                      )}
                      {idea.is_hidden && (
                        <span title="Ausgeblendet">
                          <EyeOff className="w-4 h-4 text-gray-400" />
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">von {idea.author}</p>
                  {!idea.is_owner && idea.author_info && (
                    <p className="text-xs text-blue-600 mt-1">
                      Geteilt von {idea.author_info.full_name || idea.author_info.email}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-1">
                    {new Date(idea.created_at).toLocaleDateString('de-DE')}
                  </p>
                  <div className="mt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {idea.is_owner ? (
                      <>
                        <button
                          onClick={() => handleShare(idea.id, idea.shared_with_team || false)}
                          className={`text-sm px-2 py-1 rounded flex items-center gap-1 ${
                            idea.shared_with_team 
                              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={idea.shared_with_team ? 'Freigabe entfernen' : 'Mit Team teilen'}
                        >
                          <Share2 className="w-3 h-3" />
                          {idea.shared_with_team ? 'Geteilt' : 'Teilen'}
                        </button>
                        <button
                          onClick={() => handleDelete(idea.id)}
                          className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Löschen
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleToggleVisibility(idea.id, idea.is_hidden || false)}
                        className="text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded flex items-center gap-1"
                      >
                        {idea.is_hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {idea.is_hidden ? 'Einblenden' : 'Ausblenden'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Usage Info */}
        {aiUsageInfo && aiUsageInfo.limit > 0 && (
          <div className="mt-8 text-center">
            <div className="text-sm text-gray-600">
              <span className="font-medium">{aiUsageInfo.used}/{aiUsageInfo.limit}</span> KI-Buchanalysen verwendet
              {aiUsageInfo.reset_at && calculateResetsIn(aiUsageInfo.reset_at) && (
                <span className="text-gray-500 ml-2">
                  • Zurücksetzung in {calculateResetsIn(aiUsageInfo.reset_at)} Tagen
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create QR Modal */}
      {showCreateModal && selectedChapter && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">QR-Code erstellen</h3>
            
            <div className="space-y-3 mb-6">
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">QR-Typ:</p>
                <p className="font-semibold">{(selectedChapter as any).selectedQRIdea?.idea_type}</p>
              </div>
              
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">Für Buch:</p>
                <p className="font-semibold">{selectedIdea?.book_title}</p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-medium text-gray-700">Kapitel:</p>
                <p className="font-semibold">{(selectedChapter as any).chapter_title}</p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={navigateToCreateQR}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Weiter zur Erstellung
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 